import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  Row,
  SortingState,
  Table as TableType,
  useReactTable,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTablePagination } from '@/components/ui/data-table/DataTablePagination';
import React from 'react';
import DataTableNoResult from './DataTableNoResult';
import { cn } from '@/lib/utils';
import TableRowSkeleton from './TableRowSkeleton';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  table: TableType<TData>;
  slots?: {
    pagination?: {
      isSelectPageSize?: boolean;
    };
    cell?: {
      className?: string;
    };
  };
  isLoading?: boolean;
  isHidePagination?: boolean;
  onClickRow?: (row: Row<TData>) => void;
}

export function DataTableManual<TData, TValue>({
  columns,
  table,
  slots,
  isLoading,
  isHidePagination,
  onClickRow,
}: DataTableProps<TData, TValue>) {

  return (
    <div>
      <div className="rounded-md">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        minWidth: header.column.columnDef.size,
                        maxWidth: header.column.columnDef.size,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <>
                <TableRowSkeleton col={columns.length} row={3} />
              </>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onClickRow && onClickRow(row)}
                  className={cn(onClickRow && 'cursor-pointer')}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        minWidth: cell.column.columnDef.size,
                        maxWidth: cell.column.columnDef.size,
                      }}
                      className={slots?.cell?.className}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <DataTableNoResult colSpan={columns.length} />
            )}
          </TableBody>
        </Table>
      </div>
      <div className="py-4">
        {!!table.getRowModel().rows?.length && !isHidePagination && (
          <DataTablePagination
            table={table}
            isSelectPageSize={slots?.pagination?.isSelectPageSize}
          />
        )}
      </div>
    </div>
  );
}
